/**
 * Dream Logistics Design System Tokens
 * 물류 도메인에 특화된 디자인 토큰 시스템
 */

export interface SpacingTokens {
  xs: number; // 4px
  sm: number; // 8px
  md: number; // 16px
  lg: number; // 24px
  xl: number; // 32px
  xxl: number; // 48px
}

export interface ColorTokens {
  // 물류 상태 색상
  status: {
    pending: string; // 대기중 (주황)
    confirmed: string; // 확정 (파랑)
    processing: string; // 처리중 (남색)
    shipped: string; // 출고 (청록)
    inTransit: string; // 운송중 (파랑)
    delivered: string; // 배송완료 (초록)
    delayed: string; // 지연 (빨강)
    cancelled: string; // 취소 (회색)
    returned: string; // 반품 (보라)
  };
  // 우선순위 색상
  priority: {
    low: string; // 낮음 (초록)
    medium: string; // 보통 (주황)
    high: string; // 높음 (빨강)
    urgent: string; // 긴급 (핑크)
  };
  // 브랜드 색상
  brand: {
    primary: string; // Dream TMS 브랜드 파랑
    primaryLegacy: string; // 기존 Dream 파랑 (호환성)
    secondary: string; // 포인트 빨강
    tertiary: string; // 성공 초록
    accent: string; // 강조 색상
    dreamDark: string; // Dream 다크 색상 (#003F5F)
  };
  // 대시보드 상태 카드 색상 (Figma 디자인)
  dashboard: {
    dispatchComplete: string; // 배차완료 (옐로우-그린)
    pickupComplete: string; // 픽업완료 (블루)
    deliveryComplete: string; // 배송완료 (퍼플)
  };
  // 기능적 색상
  functional: {
    background: string;
    surface: string;
    onSurface: string;
    outline: string;
    divider: string;
  };
}

export interface TypographyTokens {
  fontFamily: {
    primary: string;
    secondary: string;
    mono: string;
  };
  fontSize: {
    xs: string; // 12px
    sm: string; // 14px
    base: string; // 16px
    lg: string; // 18px
    xl: string; // 20px
    '2xl': string; // 24px
    '3xl': string; // 30px
    '4xl': string; // 36px
  };
  fontWeight: {
    light: number; // 300
    normal: number; // 400
    medium: number; // 500
    semibold: number; // 600
    bold: number; // 700
  };
  lineHeight: {
    tight: number; // 1.25
    normal: number; // 1.5
    relaxed: number; // 1.75
  };
}

export interface ShadowTokens {
  none: string;
  sm: string;
  base: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
}

export interface BreakpointTokens {
  xs: number; // 0px
  sm: number; // 600px
  md: number; // 960px
  lg: number; // 1280px
  xl: number; // 1920px
}

export interface AnimationTokens {
  duration: {
    fast: string; // 150ms
    normal: string; // 300ms
    slow: string; // 500ms
  };
  easing: {
    linear: string;
    easeIn: string;
    easeOut: string;
    easeInOut: string;
  };
}

export interface DesignTokens {
  spacing: SpacingTokens;
  colors: ColorTokens;
  typography: TypographyTokens;
  shadows: ShadowTokens;
  breakpoints: BreakpointTokens;
  animations: AnimationTokens;
}

// 토큰 값 정의
export const designTokens: DesignTokens = {
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },

  colors: {
    status: {
      pending: '#ed6c02', // 대기중 (주황)
      confirmed: '#0288d1', // 확정 (파랑)
      processing: '#1565c0', // 처리중 (남색)
      shipped: '#00acc1', // 출고 (청록)
      inTransit: '#1976d2', // 운송중 (파랑)
      delivered: '#2e7d32', // 배송완료 (초록)
      delayed: '#d32f2f', // 지연 (빨강)
      cancelled: '#757575', // 취소 (회색)
      returned: '#7b1fa2', // 반품 (보라)
    },
    priority: {
      low: '#4caf50', // 낮음 (초록)
      medium: '#ff9800', // 보통 (주황)
      high: '#f44336', // 높음 (빨강)
      urgent: '#e91e63', // 긴급 (핑크)
    },
    brand: {
      primary: '#1A5467', // Dream TMS 브랜드 다크 틸 (Figma 디자인)
      primaryLegacy: '#1976d2', // 기존 Dream 파랑 (호환성)
      secondary: '#4A90E2', // 포인트 블루 (픽업완료 카드)
      tertiary: '#2e7d32', // 성공 초록
      accent: '#C8D946', // 강조 색상 (배차완료 카드)
      dreamDark: '#003F5F', // Dream 다크 색상 (Solid Button)
    },
    dashboard: {
      dispatchComplete: '#C8D946', // 배차완료 (옐로우-그린) - Figma
      pickupComplete: '#4A90E2', // 픽업완료 (블루) - Figma
      deliveryComplete: '#8B5CF6', // 배송완료 (퍼플) - Figma
    },
    functional: {
      background: '#f5f5f5',
      surface: '#ffffff',
      onSurface: '#212121',
      outline: '#e0e0e0',
      divider: '#eeeeee',
    },
  },

  typography: {
    fontFamily: {
      primary: '"Noto Sans KR", "Roboto", "Helvetica", "Arial", sans-serif',
      secondary: '"Noto Sans KR", sans-serif',
      mono: '"Fira Code", "Monaco", "Consolas", monospace',
    },
    fontSize: {
      xs: '0.75rem', // 12px
      sm: '0.875rem', // 14px
      base: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },

  breakpoints: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },

  animations: {
    duration: {
      fast: '150ms',
      normal: '300ms',
      slow: '500ms',
    },
    easing: {
      linear: 'linear',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
};

// 편의 함수들
export const getStatusColor = (status: keyof ColorTokens['status']) =>
  designTokens.colors.status[status];

export const getPriorityColor = (priority: keyof ColorTokens['priority']) =>
  designTokens.colors.priority[priority];

export const getSpacing = (size: keyof SpacingTokens) => designTokens.spacing[size];
